<template>
  <v-app>
    <!-- Navigation Drawer -->
    <v-navigation-drawer
      v-if="isAuthenticated"
      v-model="drawer"
      app
      temporary
      :width="280"
    >
      <v-list>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-h6">
              ADM Cloud Dashboard
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ user?.username }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <v-list nav dense>
        <v-list-item
          to="/"
          exact
          prepend-icon="mdi-view-dashboard"
        >
          <v-list-item-title>Dashboard</v-list-item-title>
        </v-list-item>

        <v-list-item
          to="/kpis"
          prepend-icon="mdi-gauge"
        >
          <v-list-item-title>KPIs</v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="isAdmin"
          to="/companies"
          prepend-icon="mdi-domain"
        >
          <v-list-item-title>Companies</v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="isAdmin"
          to="/users"
          prepend-icon="mdi-account-group"
        >
          <v-list-item-title>Users</v-list-item-title>
        </v-list-item>

        <v-list-item
          to="/profile"
          prepend-icon="mdi-account"
        >
          <v-list-item-title>Profile</v-list-item-title>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-2">
          <v-btn
            block
            variant="outlined"
            prepend-icon="mdi-logout"
            @click="logout"
          >
            Logout
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- App Bar -->
    <v-app-bar
      v-if="isAuthenticated"
      app
      elevation="1"
      height="64"
      class="px-1"
    >
      <v-app-bar-nav-icon @click="drawer = !drawer" size="small"></v-app-bar-nav-icon>
      
      <v-toolbar-title class="text-h6 text-truncate d-none d-md-block mr-2" style="max-width: 200px;">
        ADM Cloud Dashboard
      </v-toolbar-title>
      <v-toolbar-title class="text-h6 d-md-none mr-2">ADM</v-toolbar-title>

      <v-spacer></v-spacer>

      <!-- Company Switcher and Controls -->
      <div class="d-flex align-center" style="max-width: 350px; min-width: 200px;">
        <company-switcher class="flex-shrink-1" />

        <!-- Theme Toggle -->
        <v-btn
          icon
          @click="toggleTheme"
          class="ml-1 flex-shrink-0"
          size="small"
          density="compact"
        >
          <v-icon size="small">{{ isDark ? 'mdi-brightness-7' : 'mdi-brightness-4' }}</v-icon>
        </v-btn>
      </div>
    </v-app-bar>

    <!-- Main Content -->
    <v-main>
      <router-view />
    </v-main>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="4000"
      location="top"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="hideSnackbar"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Loading Overlay -->
    <v-overlay
      v-model="loading"
      class="align-center justify-center"
    >
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>
  </v-app>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { useTheme } from 'vuetify'
import CompanySwitcher from '@/components/CompanySwitcher.vue'

export default {
  name: 'App',
  components: {
    CompanySwitcher
  },
  data() {
    return {
      drawer: false,
      isDark: false
    }
  },
  computed: {
    ...mapState(['loading', 'snackbar']),
    ...mapGetters('auth', ['isAuthenticated', 'user', 'isAdmin']),
    theme() {
      return useTheme()
    }
  },
  methods: {
    ...mapActions(['hideSnackbar']),
    ...mapActions('auth', ['logout', 'checkAuth']),
    toggleTheme() {
      this.isDark = !this.isDark
      this.theme.global.name.value = this.isDark ? 'dark' : 'light'
      localStorage.setItem('theme', this.isDark ? 'dark' : 'light')
    },
    async logout() {
      await this.logout()
      this.$router.push('/login')
    }
  },
  async created() {
    // Load theme preference
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      this.isDark = savedTheme === 'dark'
      this.theme.global.name.value = this.isDark ? 'dark' : 'light'
    }
    
    // Check authentication and fetch user data on app start
    if (localStorage.getItem('token')) {
      await this.checkAuth()
    }
  }
}
</script>

<style>
.v-application {
  font-family: 'Roboto', sans-serif;
}

/* Ensure header doesn't overflow */
.v-app-bar .v-toolbar__content {
  padding: 0 8px;
}

.v-app-bar .v-btn {
  margin: 0 2px;
}

/* Responsive header adjustments */
@media (max-width: 960px) {
  .v-app-bar .v-toolbar__content {
    padding: 0 4px;
  }
}
</style>